
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Location } from '@/types/locationTypes';
import { StaffLocationAssignment } from '@/types/staffLocationTypes';
import { getLocationsWithStaffAssignments, assignStaffToLocation, removeStaffFromLocation } from '@/services/staffService';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

export const useStaffLocations = (staffId: string) => {
  const { organizationId } = useAuth();
  const queryClient = useQueryClient();
  const [assignments, setAssignments] = useState<StaffLocationAssignment[]>([]);
  
  // Fetch locations and staff assignments
  const { data, isLoading, error } = useQuery({
    queryKey: ['staffLocations', staffId],
    queryFn: () => getLocationsWithStaffAssignments(staffId, organizationId || ''),
    enabled: !!staffId && !!organizationId,
  });
  
  // Create assignment mutation
  const assignMutation = useMutation({
    mutationFn: (locationId: string) => 
      assignStaffToLocation(staffId, locationId, organizationId || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffLocations', staffId] });
      toast.success('Staff member assigned to location successfully');
    },
    onError: (error) => {
      console.error('Error assigning staff to location:', error);
      toast.error('Failed to assign staff member to location');
    },
  });
  
  // Remove assignment mutation
  const removeMutation = useMutation({
    mutationFn: (locationId: string) => 
      removeStaffFromLocation(staffId, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffLocations', staffId] });
      toast.success('Staff member removed from location successfully');
    },
    onError: (error) => {
      console.error('Error removing staff from location:', error);
      toast.error('Failed to remove staff member from location');
    },
  });
  
  // Process data into assignments
  useEffect(() => {
    if (data?.locations) {
      const newAssignments = data.locations.map((location: Location) => ({
        location_id: location.id,
        location_name: location.name,
        assigned: data.assignedLocationIds.includes(location.id)
      }));
      
      setAssignments(newAssignments);
    }
  }, [data]);
  
  // Toggle a location assignment
  const toggleLocationAssignment = async (locationId: string, currentlyAssigned: boolean) => {
    if (currentlyAssigned) {
      removeMutation.mutate(locationId);
    } else {
      assignMutation.mutate(locationId);
    }
  };
  
  return {
    locations: data?.locations || [],
    assignments,
    isLoading,
    error,
    toggleLocationAssignment,
    isUpdating: assignMutation.isPending || removeMutation.isPending
  };
};
